import * as v0 from "@aidkitorg/types/lib/survey";
import { useContext, useMemo, useEffect } from "react";
import InterfaceContext from "../Context";
import { useSurveySections } from "../Contexts/SurveySectionsContext";
import { useModularMarkdown } from "../Hooks/ModularMarkdown";
import { languageContent, safeParse } from "../Util";
import { DistroQuestionProps, InfoDict } from "./Props";
import type { Question, Sections } from "../Apply";
import { AssistanceAnalysisContent, Attachment, AttachmentType, getInlineAlertContent } from "./Attachment";
import { AsyncMediaContext } from "../Contexts/AsyncMediaContext";
import { Alert, Button, Card, CardBody, Icon } from "@aidkitorg/component-library";
import { useLocalizedStrings } from "../Localization";

type InlineAlert = NonNullable<NonNullable<v0.Attachment["assistance"]>["inlineAlerts"]>[number]

type AttachmentQuestion = Question & { "Field Type": "Attachment" } & { config?: v0.Attachment, section?: NonNullable<Sections["sections"]>[number] }

export default function DocumentsSummary({
  component,
  info,
  setInfo,  
  uid,
  questionProps
}: DistroQuestionProps<v0.DocumentSummary>) {
  const L = useLocalizedStrings();
  const { sections } = useSurveySections();
  const { analysisTargetFieldsGrouped } = useContext(AsyncMediaContext);
  const context = useContext(InterfaceContext);

  const descriptionContent = component.descriptionContent[context.lang] ?? component.descriptionContent["en"] ?? "";
  const descriptionMarkdown = descriptionContent.trim() ? useModularMarkdown({
    info,
    content: descriptionContent
  }) : null;
  const noAlertsContent = component.noAlertsContent?.[context.lang] ?? component.noAlertsContent?.["en"] ?? "";
  const noAlertsMarkdown = noAlertsContent.trim() ? useModularMarkdown({
    info,
    content: noAlertsContent
  }) : null;
        
  const allAttachmentQuestions = useMemo(
    () => {  
      const questionsSectionMap = (sections?.sections ?? []).reduce(
        (acc, section) => { 
          section.Questions?.filter(q => q["Field Type"] === "Attachment").forEach(q => {
            acc.set(q, section);
          });
          return acc;
        },
        new Map<Question, NonNullable<typeof sections.sections>[number]>()
      ); 
      
      return (sections?.sections?.flatMap(section => section.Questions) ?? [])
        .filter(q => q["Field Type"] === "Attachment")
        .map(q => ({ 
          ...(q as AttachmentQuestion), 
          config: safeParse(q.Metadata!, undefined) as v0.Attachment, 
          section: questionsSectionMap.get(q)
        }));            
    },
    [sections]
  );
  
  const targetFieldDeps = analysisTargetFieldsGrouped.flatMap(({ targetField, jobTargetField }) => [
    info[targetField],
    ...(jobTargetField ? [info[jobTargetField]] : [])
  ].concat(
    allAttachmentQuestions.map(q => info[q["Target Field"]!]))
  );  
  
  // compute which attachment questions have inline alerts to show (or a loading indicator)
  const { questionsWithAlertContent, pendingJobsInProgress } = useMemo(
    () => {
      let pendingJobsInProgress = false;
      const questionsWithAlertContent = allAttachmentQuestions.filter(
        q => {
          const targetField = q["Target Field"];
          if (!targetField || !q.config.assistance || q.config.assistance.disabled) {
            return false;
          }
          const fieldValue = info[targetField] ?? "";
          const imageUrls = fieldValue.split(',')
            // this component only deals in images
            .filter(url => /\.(jpg|jpeg|gif|png|tif|tiff|bmp|webp|svg)$/.test(url));
                    
          // create a map of all analysis and job target fields
          // keyed by imageUrl
          const { analyses, jobs } = analysisTargetFieldsGrouped.reduce(
            (acc, v) => {
              const analysesByUrl = safeParse(info[v.targetField]!, null);
              for (const imageUrl of imageUrls) {
                if (analysesByUrl?.[imageUrl]) {
                  acc.analyses ??= {};
                  acc.analyses[imageUrl] ??= {};
                  acc.analyses[imageUrl][v.targetField] = analysesByUrl[imageUrl];
                }
                if (v.jobTargetField) {
                  const jobsByUrl = safeParse(info[v.jobTargetField]!, null);
                  if (jobsByUrl?.[imageUrl]) {
                    acc.jobs ??= {};
                    acc.jobs[imageUrl] ??= {};
                    acc.jobs[imageUrl][v.targetField] = typeof jobsByUrl[imageUrl] === "string" ? { jobId: jobsByUrl[imageUrl], field: v.targetField } : jobsByUrl[imageUrl];
                  }
                }
              }
              return acc;
            },
            {
              analyses: {},
              jobs: {}
            } as {
              analyses: { [imageUrl: string]: NonNullable<AttachmentType["analyses"]> },
              jobs: { [imageUrl: string]: NonNullable<AttachmentType["jobs"]> }
            }
          );
          
          function imageJobIsPending(imageUrl: string) {
            return Object.entries(jobs[imageUrl] ?? {})
              .some(([field, { jobId }]) => jobId && !analyses[imageUrl]?.[field]);
          }
          
          if (!pendingJobsInProgress) { 
            pendingJobsInProgress = imageUrls.some(imageJobIsPending);
          }

          
          // try to find a document that either has alert content, or still has a pending job running
          return imageUrls.some(
            // for each image url, grab the analysis results from info 
            // and determine if there is any alert content
            imageUrl => (q.config.assistance?.inlineAlerts ?? []).some(
              inlineAlert => getInlineAlertContent({
                lang: context.lang,
                content: inlineAlert.content,
                info: {
                  ...info,
                  ...(analyses[imageUrl] ?? {})
                },
                uid
              })
                // also check if there is a pending job running
                || imageJobIsPending(imageUrl)
            )
          );
        }
      );
      
      return {
        questionsWithAlertContent,
        pendingJobsInProgress
      }
    },
    [analysisTargetFieldsGrouped, allAttachmentQuestions, ...targetFieldDeps]
  );
  
  // set main target field
  useEffect(
    () => {
      questionProps.setInfoKey(
        component.targetField, 
        pendingJobsInProgress ? "" : new Date().toISOString(), 
        true, 
        false
      );
    },
    [questionProps.setInfoKey, component.targetField, pendingJobsInProgress]
  );
  
  if (!allAttachmentQuestions.length) {
    return <Alert
      iconName="QuestionMarkCircleIcon"
      variant="secondary">{L.ai_coach.document_signals.no_documents_uploaded}</Alert>;    
  }

  if (!questionsWithAlertContent.length) {    
    return <Alert
      iconName="CheckCircleIcon"
      variant="success">{ noAlertsMarkdown ?? L.ai_coach.document_signals.no_issues_with_your_documents }</Alert>;    
  }
  
  return <>
    { !pendingJobsInProgress && descriptionMarkdown }    
    <div className="flex flex-col gap-8">
      {  questionsWithAlertContent
        .map((q, i) => {
          const targetField = q["Target Field"];
          if (!targetField || !info[targetField]) {
            return null;
          }
          return <Card key={`doc_summary_card_${i}`}>
            <QuestionSummaryItem
              info={info}
              targetField={targetField}
              uid={uid}
              question={q} />
          </Card>
        }) }
    </div>
  </>
}

function QuestionSummaryItem({
  question,
  info,
  targetField,
  uid
}: {
  question: AttachmentQuestion,
  info: InfoDict,
  targetField: string,
  uid?: string
}) {   
  const context = useContext(InterfaceContext);  
  const content: string = (question as Record<string, any>)[languageContent(context.lang)]!;    
  const marked = useModularMarkdown({
    info,
    content
  });
  const imageUrls = info[targetField]!.split(',')
    // this component only deals in images
    .filter(url => /\.(jpg|jpeg|gif|png|tif|tiff|bmp|webp|svg)$/.test(url));  
    
  const documentItems = imageUrls.map(
    imageUrl => <DocumentSummaryItem
      key={imageUrl}
      imageUrl={imageUrl}
      info={info}
      question={question}
      targetField={targetField}
      uid={uid}
    />
  );
          
  return <>
    <div>{ marked }</div>
    {!!imageUrls.length && <div className="flex flex-col gap-4">
      { documentItems }
    </div> }
  </>
}

export function DocumentSummaryItem({ 
  imageUrl,
  info,
  targetField,
  question,
  uid,
}: { 
  imageUrl: string, 
  info: InfoDict,
  targetField: string,
  question: AttachmentQuestion
  uid?: string,
}) {
  const L = useLocalizedStrings();
  const { navigateToSection } = useSurveySections();
  const context = useContext(InterfaceContext);
  const { analysisTargetFieldsGrouped } = useContext(AsyncMediaContext);
    
  const targetFieldDeps = analysisTargetFieldsGrouped.flatMap(({ targetField, jobTargetField }) => [
    info[targetField],
    ...(jobTargetField ? [info[jobTargetField]] : [])
  ]);

  // get analysis target fields and values
  const { analyses, jobs } = useMemo(
    () => analysisTargetFieldsGrouped.reduce(
      (acc, v) => {
        const analysesByUrl = safeParse(info[v.targetField]!, null);
        if (analysesByUrl?.[imageUrl]) {
          acc.analyses ??= {};
          acc.analyses[v.targetField] = analysesByUrl[imageUrl];
        }
        if (v.jobTargetField) {
          const jobsByUrl = safeParse(info[v.jobTargetField]!, null);
          if (jobsByUrl?.[imageUrl]) {
            acc.jobs ??= {};
            acc.jobs[v.targetField] = typeof jobsByUrl[imageUrl] === "string" ? { jobId: jobsByUrl[imageUrl], field: v.targetField } : jobsByUrl[imageUrl];
          }
        }
        return acc;
      },
      {
        analyses: {},
        jobs: {}
      } as {
        analyses: AttachmentType["analyses"],
        jobs: AttachmentType["jobs"]
      }
    ),
    [...targetFieldDeps]
  );

  const assistance = question.config?.assistance;
  
  const activeInlineAlerts: NonNullable<v0.Attachment["assistance"]>["inlineAlerts"] = [];
  for (const inlineAlert of assistance?.inlineAlerts ?? []) {
    if (getInlineAlertContent({
      lang: context.lang,
      content: inlineAlert.content,
      info: { ...info, ...analyses },
      uid
    })) { 
      activeInlineAlerts.push(inlineAlert);
    }
  }
      
  const alertContent = <AssistanceAnalysisContent
    assistance={assistance}
    info={info}
    analyses={analyses}
    jobs={jobs}
    uid={uid}
  />;
  
  return <div className="flex flex-col gap-4">
    <Attachment
      url={imageUrl}
      key={imageUrl} />
    {alertContent}
    {
      (activeInlineAlerts.length > 0 && question.section?.id) && <div className="flex items-center justify-center">
        <Button
          variant="secondary"
          icon={<Icon name="ArrowLeftCircleIcon" />}
          onClick={() => {           
            navigateToSection(question.section?.id as string)
          }}>
          {L.ai_coach.document_signals.go_back_and_make_changes}
        </Button>
      </div>
    }
  </div>
}
